import React, { useMemo, useState } from "react"

import "./formationMap.scss"
import { colors } from "@/components/colors.ts"
import type { CourtField } from "@/constants/sportConfig"

const BACKGROUND_IMAGE_URL = "/images/verticalGround.svg"

interface FormationPoint {
  idfPlayer: number
  x: number
  y: number
}

interface FormationPlayer {
  idfPlayer: number
  nickname: string
  position: string
  uniformNumber: number
  isStarting: boolean
}

function getConvexHull(points) {
  if (points.length < 3) {
    return []
  }

  const startPoint = points.reduce((lowest, point) => {
    if (
      point.pixelY > lowest.pixelY ||
      (point.pixelY === lowest.pixelY && point.pixelX < lowest.pixelX)
    ) {
      return point
    }

    return lowest
  })

  const sortedPoints = [...points].sort((a, b) => {
    if (a === startPoint) {
      return -1
    }
    if (b === startPoint) {
      return 1
    }

    const angleA = Math.atan2(a.pixelY - startPoint.pixelY, a.pixelX - startPoint.pixelX)
    const angleB = Math.atan2(b.pixelY - startPoint.pixelY, b.pixelX - startPoint.pixelX)

    if (angleA !== angleB) {
      return angleA - angleB
    }

    const distA = Math.hypot(a.pixelX - startPoint.pixelX, a.pixelY - startPoint.pixelY)
    const distB = Math.hypot(b.pixelX - startPoint.pixelX, b.pixelY - startPoint.pixelY)

    return distA - distB
  })

  const ccw = (p1, p2, p3) => {
    return (
      (p2.pixelX - p1.pixelX) * (p3.pixelY - p1.pixelY) -
      (p2.pixelY - p1.pixelY) * (p3.pixelX - p1.pixelX)
    )
  }

  const hull = []

  for (const point of sortedPoints) {
    while (hull.length >= 2 && ccw(hull[hull.length - 2], hull[hull.length - 1], point) <= 0) {
      hull.pop()
    }
    hull.push(point)
  }

  return hull
}

export default function FormationMap({
  formationPointList,
  playerList,
  width,
  height,
  stadiumX,
  stadiumY,
  backgroundSvg = BACKGROUND_IMAGE_URL,
  courtField = { left: 0, top: 0, right: 1, bottom: 1 },
  showMeasurements = true,
}: {
  formationPointList: FormationPoint[]
  playerList: FormationPlayer[]
  width: number
  height: number
  stadiumX: number
  stadiumY: number
  backgroundSvg?: string
  // 이미지(width×height) 안에서 실제 경기장 영역(0~1 비율). 기본 full-bleed = 기존 동작.
  courtField?: CourtField
  // 거리 측정선·수치 표기 노출 여부. 기본 표시(럭비 등은 숨김).
  showMeasurements?: boolean
}) {
  // 경기장 영역의 픽셀 경계 (코트 SVG가 여백을 두고 그려진 종목 대응)
  const fieldLeft = courtField.left * width
  const fieldRight = courtField.right * width
  const fieldTop = courtField.top * height
  const fieldBottom = courtField.bottom * height
  const fieldW = fieldRight - fieldLeft
  const fieldH = fieldBottom - fieldTop
  const processedPlayers = useMemo(() => {
    const hexToRgba = (hex: string, alpha = 1) => {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)

      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }

    if (!formationPointList.length || !playerList.length) {
      return []
    }

    return formationPointList
      .map((point) => {
        const player = playerList.find((p) => p.idfPlayer === point.idfPlayer)
        if (!player) {
          return null
        }

        return {
          ...point,
          ...player,
          pixelX: fieldLeft + (point.y / stadiumY) * fieldW,
          pixelY: fieldTop + (point.x / stadiumX) * fieldH,
          isStarting: player.isStarting,
          backgroundColor: !player.isStarting ? hexToRgba(colors.red[700], 0.5) : colors.red[700],
        }
      })
      .filter(Boolean)
  }, [formationPointList, playerList, stadiumX, stadiumY, fieldLeft, fieldTop, fieldW, fieldH])

  const polygonPoints = useMemo(() => {
    if (processedPlayers.length < 3) {
      return ""
    }

    const hull = getConvexHull(processedPlayers)

    return hull.map((p) => `${p.pixelX},${p.pixelY}`).join(" ")
  }, [processedPlayers])

  const measurements = useMemo(() => {
    if (processedPlayers.length < 3) {
      return null
    }

    const minX = Math.min(...processedPlayers.map((p) => p.pixelX))
    const maxX = Math.max(...processedPlayers.map((p) => p.pixelX))
    const minY = Math.min(...processedPlayers.map((p) => p.pixelY))
    const maxY = Math.max(...processedPlayers.map((p) => p.pixelY))

    const pixelToMeterX = stadiumY / 100 / fieldW
    const pixelToMeterY = stadiumX / 100 / fieldH

    // 거리는 이미지 가장자리가 아니라 "경기장 영역(field-rect)" 경계 기준으로 측정
    const topDistance = Math.round((minY - fieldTop) * pixelToMeterY * 10) / 10
    const centerDistance = Math.round((fieldTop + fieldH / 2 - maxY) * pixelToMeterY * 10) / 10
    const bottomDistance = Math.round((fieldBottom - maxY) * pixelToMeterY * 10) / 10
    const leftDistance = Math.round((minX - fieldLeft) * pixelToMeterX * 10) / 10
    const rightDistance = Math.round((fieldRight - maxX) * pixelToMeterX * 10) / 10
    const widthMeters = Math.round((maxX - minX) * pixelToMeterX * 10) / 10
    const heightMeters = Math.round((maxY - minY) * pixelToMeterY * 10) / 10

    return {
      minX,
      maxX,
      minY,
      maxY,
      topDistance,
      centerDistance: Math.abs(centerDistance),
      bottomDistance,
      leftDistance,
      rightDistance,
      width: widthMeters,
      height: heightMeters,
    }
  }, [
    processedPlayers,
    stadiumX,
    stadiumY,
    fieldLeft,
    fieldRight,
    fieldTop,
    fieldBottom,
    fieldW,
    fieldH,
  ])

  const teamArea = useMemo(() => {
    if (processedPlayers.length < 3) {
      return 0
    }

    const hull = getConvexHull(processedPlayers)
    if (hull.length < 3) {
      return 0
    }

    let area = 0
    for (let i = 0; i < hull.length; i++) {
      const j = (i + 1) % hull.length
      area += hull[i].pixelX * hull[j].pixelY
      area -= hull[j].pixelX * hull[i].pixelY
    }
    area = Math.abs(area) / 2

    const pixelToMeter2 = (stadiumY / 100 / width) * (stadiumX / 100 / height)

    return Math.round(area * pixelToMeter2 * 10) / 10
  }, [processedPlayers, stadiumX, stadiumY, width, height])

  return (
    <div className="FormationMap" style={{ width: width + "px", height: height + "px" }}>
      <img className="formation-map-background" src={backgroundSvg} alt="Pitch" />

      {polygonPoints && (
        <svg className="formation-map-polygon">
          <polygon points={polygonPoints} />
        </svg>
      )}

      {processedPlayers.map(({ idfPlayer, pixelX, pixelY, uniformNumber, backgroundColor }) => (
        <div
          key={idfPlayer}
          className="formation-map-player"
          style={{
            left: pixelX + "px",
            top: pixelY + "px",
            transform: "translate(-50%, -50%)",
            backgroundColor: backgroundColor,
          }}>
          <span>{uniformNumber}</span>
        </div>
      ))}

      <div className="formation-map-substitute-container">
        <div className="formation-map-substitute-line">
          <span className="formation-map-substitute-label">
            <span className="formation-map-substitute-label-icon starting"></span>
            Starting
          </span>
          <span className="formation-map-substitute-label">
            <span className="formation-map-substitute-label-icon substitute"></span>
            Substitute
          </span>
        </div>
      </div>

      {teamArea > 0 && measurements && (
        <div
          className="formation-map-team-area"
          style={{
            left: (measurements.minX + measurements.maxX) / 2 - 30 + "px",
            top: measurements.minY - 25 + "px",
          }}>
          {teamArea} m²
        </div>
      )}

      {showMeasurements && measurements && (
        <>
          <svg className="formation-map-measurements">
            {/* 화살표 정의 */}
            <defs>
              <marker
                id="arrow-start"
                markerWidth="10"
                markerHeight="10"
                refX="0"
                refY="5"
                orient="auto">
                <path
                  d="M0,5 L5,2 M0,5 L5,8"
                  stroke={colors.pink["700"]}
                  strokeWidth="1.5"
                  fill="none"
                />
              </marker>
              <marker
                id="arrow-end"
                markerWidth="10"
                markerHeight="10"
                refX="10"
                refY="5"
                orient="auto">
                <path
                  d="M10,5 L5,2 M10,5 L5,8"
                  stroke={colors.pink["700"]}
                  strokeWidth="1.5"
                  fill="none"
                />
              </marker>
            </defs>

            {/* 격자선 (경기장 영역 기준) */}
            <line
              className="formation-map-grid-line"
              x1={fieldLeft}
              y1={measurements.minY}
              x2={fieldRight}
              y2={measurements.minY}
            />
            <line
              className="formation-map-grid-line"
              x1={fieldLeft}
              y1={measurements.maxY}
              x2={fieldRight}
              y2={measurements.maxY}
            />
            <line
              className="formation-map-grid-line"
              x1={fieldLeft}
              y1={fieldTop + fieldH / 2}
              x2={fieldRight}
              y2={fieldTop + fieldH / 2}
            />
            <line
              className="formation-map-grid-line"
              x1={measurements.minX}
              y1={fieldTop}
              x2={measurements.minX}
              y2={fieldBottom}
            />
            <line
              className="formation-map-grid-line"
              x1={measurements.maxX}
              y1={fieldTop}
              x2={measurements.maxX}
              y2={fieldBottom}
            />

            {/* 측정선 - 상단 (필드 상단 → 최상단 선수) */}
            <line
              className="formation-map-measure-line"
              x1={fieldRight - 20}
              y1={fieldTop}
              x2={fieldRight - 20}
              y2={measurements.minY}
              markerStart="url(#arrow-start)"
              markerEnd="url(#arrow-end)"
            />

            {/* 측정선 - 중앙 (최상단 선수 → 최하단 선수) = 팀 높이 */}
            <line
              className="formation-map-measure-line"
              x1={fieldRight - 20}
              y1={measurements.minY}
              x2={fieldRight - 20}
              y2={measurements.maxY}
              markerStart="url(#arrow-start)"
              markerEnd="url(#arrow-end)"
            />

            {/* 측정선 - 하단 (최하단 선수 → 필드 하단) */}
            <line
              className="formation-map-measure-line"
              x1={fieldRight - 20}
              y1={measurements.maxY}
              x2={fieldRight - 20}
              y2={fieldBottom}
              markerStart="url(#arrow-start)"
              markerEnd="url(#arrow-end)"
            />

            {/* 측정선 - 좌측 (필드 왼쪽 → 최좌측 선수) */}
            <line
              className="formation-map-measure-line"
              x1={fieldLeft}
              y1={fieldBottom - 20}
              x2={measurements.minX}
              y2={fieldBottom - 20}
              markerStart="url(#arrow-start)"
              markerEnd="url(#arrow-end)"
            />

            {/* 측정선 - 중앙 (최좌측 선수 → 최우측 선수) = 팀 너비 */}
            <line
              className="formation-map-measure-line"
              x1={measurements.minX}
              y1={fieldBottom - 20}
              x2={measurements.maxX}
              y2={fieldBottom - 20}
              markerStart="url(#arrow-start)"
              markerEnd="url(#arrow-end)"
            />

            {/* 측정선 - 우측 (최우측 선수 → 필드 오른쪽) */}
            <line
              className="formation-map-measure-line"
              x1={measurements.maxX}
              y1={fieldBottom - 20}
              x2={fieldRight}
              y2={fieldBottom - 20}
              markerStart="url(#arrow-start)"
              markerEnd="url(#arrow-end)"
            />
          </svg>

          <div
            className="formation-map-measure-text top"
            style={{
              top: (fieldTop + measurements.minY) / 2 - 8 + "px",
            }}>
            {measurements.topDistance} m
          </div>

          <div
            className="formation-map-measure-text center"
            style={{
              top: (measurements.minY + measurements.maxY) / 2 - 8 + "px",
            }}>
            {measurements.height} m
          </div>

          <div
            className="formation-map-measure-text bottom"
            style={{
              top: (measurements.maxY + fieldBottom) / 2 - 8 + "px",
            }}>
            {measurements.bottomDistance} m
          </div>

          <div
            className="formation-map-measure-text left"
            style={{
              left: (fieldLeft + measurements.minX) / 2 - 15 + "px",
              bottom: "5px",
            }}>
            {measurements.leftDistance} m
          </div>

          <div
            className="formation-map-measure-text center-width"
            style={{
              left: (measurements.minX + measurements.maxX) / 2 - 20 + "px",
              bottom: "5px",
            }}>
            {measurements.width} m {/* 팀 너비 */}
          </div>

          <div
            className="formation-map-measure-text right"
            style={{
              left: (measurements.maxX + fieldRight) / 2 - 15 + "px",
              bottom: "5px",
            }}>
            {measurements.rightDistance} m
          </div>
        </>
      )}
    </div>
  )
}
